import React, { useEffect, useState } from "react";
import AdminLayout from "../../components/admin/AdminLayout";
import { api, money, formatApiError } from "../../lib/api";
import { StatusPill } from "./Dashboard";
import { toast } from "sonner";

const SHIPPING_OPTIONS = ["not-shipped", "processing", "shipped", "delivered"];

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tracking, setTracking] = useState("");

  useEffect(() => { setTracking(active?.tracking_number || ""); }, [active]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/orders");
      setOrders(data.orders);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const updateShipping = async (orderId, status) => {
    try {
      const { data } = await api.patch(`/admin/orders/${orderId}/shipping`, { shipping_status: status });
      toast.success("Shipping updated");
      setActive(data.order);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveTracking = async (orderId) => {
    try {
      const { data } = await api.patch(`/admin/orders/${orderId}/shipping`, { tracking_number: tracking });
      toast.success("Tracking saved");
      setActive(data.order);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <AdminLayout title="Orders">
      <div className="bg-white border border-zinc-200 rounded-md overflow-hidden" data-testid="orders-table">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 bg-zinc-50">
            <tr>
              <th className="text-left px-5 py-3">Order</th>
              <th className="text-left px-5 py-3">Date</th>
              <th className="text-left px-5 py-3">Customer</th>
              <th className="text-left px-5 py-3">Items</th>
              <th className="text-left px-5 py-3">Payment</th>
              <th className="text-left px-5 py-3">Shipping</th>
              <th className="text-right px-5 py-3">Total</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="text-center text-zinc-500 py-10">Loading…</td></tr>}
            {!loading && orders.length === 0 && <tr><td colSpan={7} className="text-center text-zinc-500 py-10">No orders yet.</td></tr>}
            {orders.map((o) => (
              <tr key={o.id} onClick={() => setActive(o)} data-testid={`order-row-${o.id.slice(0,8)}`} className="border-t border-zinc-100 hover:bg-zinc-50 cursor-pointer">
                <td className="px-5 py-3 font-mono text-xs">{o.id.slice(0, 8)}</td>
                <td className="px-5 py-3 text-zinc-600 text-xs font-mono">{new Date(o.created_at).toLocaleString()}</td>
                <td className="px-5 py-3">{o.customer_email || "—"}</td>
                <td className="px-5 py-3">{o.items?.length || 0}</td>
                <td className="px-5 py-3"><StatusPill status={o.payment_status} /></td>
                <td className="px-5 py-3 text-xs">{o.shipping_status}</td>
                <td className="px-5 py-3 text-right font-mono">{money(o.subtotal)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Side drawer */}
      {active && (
        <>
          <div className="fixed inset-0 bg-black/30 z-40" onClick={() => setActive(null)} />
          <div className="fixed top-0 right-0 z-50 h-full w-full max-w-lg bg-white border-l border-zinc-200 shadow-xl flex flex-col" data-testid="order-detail">
            <div className="px-6 py-5 border-b border-zinc-200 flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">Order</div>
                <div className="font-mono text-sm">{active.id}</div>
              </div>
              <button onClick={() => setActive(null)} className="text-zinc-500 hover:text-zinc-900">Close</button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold mb-2">Customer</div>
                <div className="text-sm">{active.customer_email || "Guest checkout"}</div>
                {active.customer_name && <div className="text-sm text-zinc-600">{active.customer_name}</div>}
                {active.customer_phone && <div className="text-sm text-zinc-600" data-testid="order-customer-phone">{active.customer_phone}</div>}
              </div>
              {active.shipping_address && (
                <div data-testid="order-shipping-address">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold mb-2">Ship to</div>
                  <div className="text-sm text-zinc-700 leading-relaxed">
                    {active.shipping_address.name && <div>{active.shipping_address.name}</div>}
                    {active.shipping_address.line1 && <div>{active.shipping_address.line1}</div>}
                    {active.shipping_address.line2 && <div>{active.shipping_address.line2}</div>}
                    <div>
                      {[[active.shipping_address.city, active.shipping_address.state].filter(Boolean).join(", "), active.shipping_address.postal_code].filter(Boolean).join(" ")}
                    </div>
                    {active.shipping_address.country && <div>{active.shipping_address.country}</div>}
                  </div>
                </div>
              )}
              {active.notes && (
                <div data-testid="order-notes">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold mb-2">Order notes</div>
                  <div className="text-sm bg-amber-50 border border-amber-200 text-amber-900 rounded p-3 whitespace-pre-wrap break-words">
                    {active.notes}
                  </div>
                </div>
              )}
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold mb-2">Items</div>
                <ul className="space-y-2">
                  {active.items?.map((it) => (
                    <li key={it.product_id} className="flex items-start justify-between text-sm border-b border-zinc-100 pb-2">
                      <div>
                        <div>{it.name}</div>
                        <div className="text-xs text-zinc-500 font-mono">{it.sku} × {it.quantity}</div>
                      </div>
                      <div className="font-mono">{money(it.price * it.quantity)}</div>
                    </li>
                  ))}
                </ul>
                <div className="flex items-center justify-between pt-3 font-mono">
                  <span className="text-zinc-500 text-sm">Subtotal</span>
                  <span>{money(active.subtotal)}</span>
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold mb-2">Payment status</div>
                <StatusPill status={active.payment_status} />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold mb-2">Shipping status</div>
                <div className="flex flex-wrap gap-2">
                  {SHIPPING_OPTIONS.map((s) => (
                    <button
                      key={s}
                      data-testid={`order-ship-${s}`}
                      onClick={() => updateShipping(active.id, s)}
                      className={`text-xs px-3 py-2 border rounded-sm ${active.shipping_status === s ? "bg-zinc-900 text-white border-zinc-900" : "border-zinc-200 hover:border-zinc-400"}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              <div data-testid="order-shipping-details">
                <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold mb-2">Shipping details</div>
                <div className="text-sm space-y-1 mb-3">
                  <div className="flex justify-between"><span className="text-zinc-500">Method</span><span>{active.shipping_method_label || "—"}</span></div>
                  <div className="flex justify-between"><span className="text-zinc-500">Carrier</span><span className="uppercase">{active.shipping_carrier || "—"}</span></div>
                  <div className="flex justify-between"><span className="text-zinc-500">Shipping cost</span><span className="font-mono">{money(active.shipping_cost || 0)}</span></div>
                  {active.freight && <div className="text-amber-700 text-xs font-medium">Freight shipment</div>}
                </div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold mb-1 block">Tracking number</label>
                <div className="flex gap-2">
                  <input
                    data-testid="order-tracking-input"
                    value={tracking}
                    onChange={(e) => setTracking(e.target.value)}
                    placeholder="Enter tracking #"
                    className="flex-1 h-9 px-3 bg-white border border-zinc-300 rounded text-sm focus:outline-none focus:border-zinc-900"
                  />
                  <button data-testid="order-tracking-save" onClick={() => saveTracking(active.id)} className="text-sm px-4 h-9 rounded bg-zinc-900 text-white hover:bg-zinc-800">Save</button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </AdminLayout>
  );
}
